import {
  createThetaClient,
  ThetaError,
  type components,
  type ErrorField,
  type Me,
  type Page,
  type Pollable,
} from "@theta/api-client";

/**
 * The /api/v1 client, configured once for the web app.
 *
 * The only file that knows how the client is constructed: cookie mode,
 * same-origin base URL, and the `X-Theta-Client` header that replaces the CSRF
 * token (D6). Screens import `thetaApi`, never `createThetaClient`.
 *
 * Every type a screen reads — `Me` among them — is generated from the backend's
 * Zod schemas, so a field renamed in `backend/src/schemas/user.ts` fails
 * `pnpm typecheck` here once `openapi.json` and `schema.d.ts` are regenerated.
 * Those types are imported from `@theta/api-client` at the point of use; this
 * file re-exports none of them. `lib/legacy-api.ts` and `lib/types.ts` are
 * gone, and every module left in `lib/api/` speaks v1 only (§5.1).
 */
/**
 * The page's own origin, spelled out.
 *
 * `${origin}/api/v1` resolves to exactly the URL a bare `/api/v1` does, since
 * the origin is the document's. Naming it costs nothing in the browser and buys
 * one thing outside it: `Request` in a non-browser realm (a jsdom test, a Node
 * script) has no document to resolve a relative path against and rejects it, so
 * a test would otherwise have to build its own client instead of this one.
 */
const SAME_ORIGIN = typeof window === "undefined" ? "" : window.location.origin;

export const thetaApi = createThetaClient({
  // Same-origin: `next.config.ts` proxies the backend, so no CORS and no
  // cross-site cookie ever enters the picture.
  baseUrl: `${SAME_ORIGIN}/api/v1`,
  auth: { mode: "cookie" },
  clientId: "theta-web/0.2.0",
  /**
   * Late-bound on purpose. `createThetaClient` captures `globalThis.fetch` once
   * at construction — module-import time here, before anything a test installs.
   * Going through the global on every request uses the transport in effect at
   * call time, so an msw interceptor (or a service worker, or an instrumentation
   * wrapper) sees these requests instead of being bypassed.
   */
  fetch: (input, init) => globalThis.fetch(input, init),
});

/**
 * The number the header badge shows: notifications plus DM conversations with
 * something unread. Both counters come from `GET /me` (§1.10 makes it one
 * document precisely so the header needs no second poll), and the addition is
 * here rather than in a component so there is one answer.
 *
 * Rename either counter in `backend/src/schemas/user.ts` and, after
 * `openapi:dump` + `generate`, this line stops compiling.
 */
export function unreadBadgeTotal(me: Me): number {
  return me.badges.unreadNotificationCount + me.badges.unreadConversationCount;
}

/**
 * The page-limit ceiling — one number, pinned to the contract.
 *
 * Every paged read on /api/v1 declares the same `limit` schema —
 * `z.coerce.number().int().min(1).max(100)` inside a `z.strictObject` — so a
 * `limit` of 101 is not a slightly-too-large page but a 400 `badRequest` with no
 * document at all. Two call sites shipped `limit: 200` and 400ed on every
 * request: the whole comment thread (a post card claiming "14 kommentarer" above
 * an empty one, re-requested every second by the 1 Hz poll) and every
 * custom-emoji read (an empty reaction picker, no `:shortcode:` autocomplete,
 * and an admin told a group with 30 emoji has none).
 *
 * A constant rather than "change 200 to 100" because a magic number in a hook
 * cannot know what the server accepts. `openapi.json` does, but
 * openapi-typescript emits `number` for an integer with a `maximum`, so this
 * ceiling is the one part of the contract the generated types cannot carry into
 * the compiler. `lib/api/__tests__/pageLimits.test.ts` holds it from both sides:
 *
 *   1. it reads `backend/openapi.json` and asserts every route's `limit` maximum
 *      equals this number — raise the cap on the server and this file fails, not
 *      a screen;
 *   2. it scans `lib/`, `app/` and `components/` for a numeric `limit` literal
 *      and fails on any that exceeds it, so a new `limit: 200` cannot reach a
 *      user without turning `pnpm test` red first.
 *
 * `pageLimit()` is the belt to those braces: anything that slips past both is
 * clamped rather than sent. A truncated first page is a bad page; a 400 is no
 * page, and on a polled read one 400 per second for as long as the tab is open.
 */
export const MAX_PAGE_LIMIT = 100;

/**
 * Clamp a requested page size to what the contract accepts.
 *
 * Callers pass what they want and the ceiling is applied here. Getting past 100
 * rows means walking the cursor (`hasMore` / `nextCursor`), as the comment
 * thread does.
 */
export function pageLimit(requested: number): number {
  if (!Number.isFinite(requested)) return MAX_PAGE_LIMIT;
  const whole = Math.floor(requested);
  if (whole < 1) return 1;
  return Math.min(whole, MAX_PAGE_LIMIT);
}

/**
 * The envelope, for the routes the client has no method for yet.
 *
 * `@theta/api-client` hand-writes a method per route and has ten — `meta`, `me`,
 * `user`, `userMessaging`, `group`, `groupPosts`, `createPost`, `linkPreview`,
 * `setReaction`, `removeReaction`. The content domain needs sixteen more
 * (`/feed`, `/posts/{id}`, its comments, its polls, `/uploads`, the group's
 * emoji and documents), and the client's own answer is `client.http`.
 *
 * `http` gives the request side of the contract for free: a path that does not
 * exist, or a query parameter a route does not take, is a compile error. What it
 * does not give is what the facade's methods do — unwrapping `{data, meta}` and
 * turning a non-2xx into a `ThetaError` with a narrowable `code` — because
 * `unwrap` is module-private in the client. These two functions are that and
 * nothing else: no caching, no retries, no reshaping. They belong in
 * `packages/api-client` and are here only because this batch may not edit it;
 * `blockedOn` says so.
 *
 * The response type is named from `components["schemas"]` for the reason the
 * client names it — `openapi-fetch`'s `Readable<T>` walks branded ids with a
 * homomorphic mapped type and destroys every `EntityId<…>` on the way out.
 */
interface V1Envelope<T> {
  data: T;
  page?: { nextCursor: string | null; prevCursor: string | null; hasMore: boolean };
  meta: components["schemas"]["ResponseMeta"];
}

type FetchResult = { data?: unknown; error?: unknown; response: Response };

/** The one place a raw `http.*` result stops being HTTP and becomes a value or a throw. */
export function unwrapV1<T>(result: FetchResult): V1Envelope<T> {
  const { data, error, response } = result;
  // A 204 is a success with no document — `DELETE /posts/{id}` and
  // `DELETE /documents/{id}` answer one. Reading its absent body as a malformed
  // 2xx would turn every successful delete into an `internalError`.
  if (response.ok && response.status === 204) {
    return { data: null as T, meta: { requestId: "" } };
  }
  if (error !== undefined || !response.ok || data === undefined) {
    const body = (error ?? {}) as {
      error?: { code?: string; message?: string; fields?: ErrorField[] | null; retryAfter?: number | null };
      meta?: { requestId?: string };
    };
    throw new ThetaError({
      code: body.error?.code ?? "internalError",
      message: body.error?.message ?? `HTTP ${response.status}`,
      status: response.status,
      fields: body.error?.fields ?? [],
      requestId: body.meta?.requestId ?? null,
      retryAfter: body.error?.retryAfter ?? null,
    });
  }
  return data as V1Envelope<T>;
}

/** `data` alone, for a route that returns one document. */
export function dataOf<T>(result: FetchResult): T {
  return unwrapV1<T>(result).data;
}

/** A collection envelope in the shape `useInfiniteQuery` consumes (§1.3). */
export function pageOf<T>(result: FetchResult): Page<T> {
  const envelope = unwrapV1<T[] | null>(result);
  return {
    items: envelope.data ?? [],
    nextCursor: envelope.page?.nextCursor ?? null,
    prevCursor: envelope.page?.prevCursor ?? null,
    hasMore: envelope.page?.hasMore ?? false,
  };
}

/**
 * The pollable form of one document (§1.10). `broadcasts.ts` had it inline and
 * `getPost` needed the same nine lines, so it lives here beside
 * `pollablePageOf`. The discriminant stops a caller reading `.data` off an
 * `unchanged` tick.
 */
export function pollableOf<T>(result: FetchResult): Pollable<T> {
  const envelope = unwrapV1<T>(result);
  const pollAfterMs = envelope.meta.pollAfterMs ?? null;
  if (envelope.meta.unchanged) {
    return { unchanged: true, state: envelope.meta.state ?? "", pollAfterMs };
  }
  return {
    unchanged: false,
    data: envelope.data,
    state: envelope.meta.state ?? null,
    pollAfterMs,
  };
}

/** The pollable form of a collection — `/feed` is both. */
export function pollablePageOf<T>(result: FetchResult): Pollable<Page<T>> {
  const envelope = unwrapV1<T[] | null>(result);
  const pollAfterMs = envelope.meta.pollAfterMs ?? null;
  if (envelope.meta.unchanged) {
    return { unchanged: true, state: envelope.meta.state ?? "", pollAfterMs };
  }
  return {
    unchanged: false,
    data: {
      items: envelope.data ?? [],
      nextCursor: envelope.page?.nextCursor ?? null,
      prevCursor: envelope.page?.prevCursor ?? null,
      hasMore: envelope.page?.hasMore ?? false,
    },
    state: envelope.meta.state ?? null,
    pollAfterMs,
  };
}
